// טיימליין: מקטע לכל שכבה (גרירה/שינוי אורך/הקלדה), כניסה ויציאה, keyframes, ניגון ו-playhead,
// משך, לולאות, פריים סיום מפורש ושינוי מהירות כולל (setSpeed) — כל אלה בלי AI.
import { memo, useEffect, useRef, useState } from 'react';
import type { BannerDoc, Layer, Transition } from '@bb/schema';
import { trackOf, updateLayer, userOps } from './docUtils';
import { fmtMs } from './types';

interface Props {
  doc: BannerDoc;
  elapsed: number;
  playing: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
  onSeek: (ms: number) => void;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  onChange: (doc: BannerDoc, key?: string) => void;
  spec: Record<string, any> | null;
  onError: (msg: string) => void;
}

function NumIn({ label, value, onCommit, min = 0, max, step = 10, width = 76 }: { label: string; value: number; onCommit: (n: number) => void; min?: number; max?: number; step?: number; width?: number }) {
  const [s, setS] = useState(String(value));
  useEffect(() => setS(String(value)), [value]);
  const commit = () => { const n = Number(s); if (Number.isFinite(n) && n !== value) onCommit(Math.max(min, max !== undefined ? Math.min(max, n) : n)); else setS(String(value)); };
  return (
    <label className="tl-num"><span>{label}</span>
      <input type="number" className="mono" style={{ width }} value={s} min={min} max={max} step={step} onChange={(e) => setS(e.target.value)} onBlur={commit} onKeyDown={(e) => { if (e.key === 'Enter') commit(); }} />
    </label>
  );
}

const SNAP = 50;

function Timeline({ doc, elapsed, playing, onPlay, onPause, onStop, onSeek, selectedId, onSelect, onChange, spec, onError }: Props) {
  const D = doc.durationMs;
  const ta = globalThis.BBRuntime ? globalThis.BBRuntime.timeAt(doc, elapsed) : { t: elapsed % D, loop: 0, finished: false };
  const lanes = useRef<HTMLDivElement>(null);
  const drag = useRef<{ id: string; mode: 'move' | 'start' | 'end'; x0: number; s0: number; e0: number; key: string } | null>(null);
  const scrub = useRef(false);
  const latest = useRef({ doc, onChange, onSeek });
  latest.current = { doc, onChange, onSeek };
  const [speed, setSpeed] = useState('1.25');

  const msFromClientX = (cx: number) => {
    const r = lanes.current!.getBoundingClientRect();
    return Math.min(1, Math.max(0, (cx - r.left) / r.width)) * latest.current.doc.durationMs;
  };

  useEffect(() => {
    const move = (e: PointerEvent) => {
      if (scrub.current) { latest.current.onSeek(Math.round(msFromClientX(e.clientX))); return; }
      const d = drag.current;
      if (!d) return;
      const { doc: cur, onChange: change } = latest.current;
      const r = lanes.current!.getBoundingClientRect();
      const q = e.altKey ? 10 : SNAP;
      const dms = Math.round(((e.clientX - d.x0) / r.width) * cur.durationMs / q) * q;
      let s = d.s0, en = d.e0;
      if (d.mode === 'move') { const len = d.e0 - d.s0; s = Math.min(Math.max(0, d.s0 + dms), cur.durationMs - len); en = s + len; }
      if (d.mode === 'start') s = Math.min(Math.max(0, d.s0 + dms), d.e0 - q);
      if (d.mode === 'end') en = Math.max(d.s0 + q, Math.min(cur.durationMs, d.e0 + dms));
      change(updateLayer(cur, d.id, (l) => { l.startMs = s; l.endMs = en >= cur.durationMs && l.endMs == null ? null : en; }), d.key);
    };
    const up = () => { drag.current = null; scrub.current = false; };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    return () => { window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const startDrag = (e: React.PointerEvent, l: Layer, mode: 'move' | 'start' | 'end') => {
    e.stopPropagation(); e.preventDefault();
    onSelect(l.id);
    drag.current = { id: l.id, mode, x0: e.clientX, s0: l.startMs, e0: l.endMs ?? D, key: `tl-${mode}-${l.id}-${Date.now()}` };
  };

  const ops = (o: Parameters<typeof userOps>[1], key?: string) => {
    const r = userOps(doc, o);
    if (r.error) onError(r.error); else onChange(r.doc!, key);
  };

  // בדיקת משך מול המפרט — רצה מחדש בכל שינוי (כולל שינוי מהירות)
  const maxD = typeof spec?.maxDurationMs === 'number' ? spec.maxDurationMs : null;
  const maxL = typeof spec?.maxLoops === 'number' ? spec.maxLoops : null;
  const total = doc.loops === 0 ? Infinity : D * doc.loops;
  const warns: string[] = [];
  if (maxD !== null && total > maxD) warns.push(doc.loops === 0 ? `לולאה אינסופית — המפרט מגביל משך כולל ל-${fmtMs(maxD)}` : `משך כולל ${fmtMs(total)} (${fmtMs(D)}×${doc.loops}) עולה על המקסימום במפרט ${fmtMs(maxD)}`);
  if (maxL !== null && (doc.loops === 0 || doc.loops > maxL)) warns.push(`מספר הלולאות (${doc.loops === 0 ? '∞' : doc.loops}) עולה על המותר במפרט (${maxL})`);

  const step = D <= 5000 ? 500 : D <= 20000 ? 1000 : 5000;
  const ticks = Array.from({ length: Math.floor(D / step) + 1 }, (_, i) => i * step);
  const layers = [...doc.layers].sort((a, b) => b.zIndex - a.zIndex);
  const sel = doc.layers.find((l) => l.id === selectedId) ?? null;
  const pct = (ms: number) => `${(ms / D) * 100}%`;

  return (
    <section className="ed-panel tl" aria-labelledby="tl-h">
      <div className="tl-top">
        <h3 id="tl-h" className="ed-h" style={{ margin: 0 }}>טיימליין</h3>
        <div className="row" style={{ gap: 4 }}>
          {playing ? <button className="btn small" onClick={onPause} aria-label="השהיה">⏸ השהיה</button> : <button className="btn small primary" onClick={onPlay} aria-label="ניגון">▶ ניגון</button>}
          <button className="btn small" onClick={onStop} aria-label="עצירה וחזרה להתחלה">⏹ עצירה</button>
        </div>
        <span className="mono tl-time" aria-live="off">{fmtMs(ta.t)} / {fmtMs(D)}{doc.loops !== 1 ? ` · לולאה ${ta.loop + 1}/${doc.loops === 0 ? '∞' : doc.loops}` : ''}{ta.finished ? ' · פריים סיום' : ''}</span>
        <input type="range" aria-label="playhead (מילישניות)" min={0} max={D} step={10} value={Math.round(ta.t)} onChange={(e) => onSeek(Number(e.target.value))} style={{ flex: 1, minWidth: 120 }} />
      </div>

      <div className="tl-banner row">
        <NumIn label="משך לולאה (ms)" value={D} min={100} max={120000} step={100} onCommit={(n) => ops([{ op: 'setBannerTiming', durationMs: Math.round(n) }])} />
        <NumIn label="לולאות (0=∞)" value={doc.loops} min={0} max={100} step={1} width={56} onCommit={(n) => ops([{ op: 'setBannerTiming', loops: Math.round(n) }])} />
        <label className="tl-num"><span>פריים סיום מפורש</span>
          <input type="checkbox" checked={doc.endFrameMs != null} onChange={(e) => ops([{ op: 'setBannerTiming', endFrameMs: e.target.checked ? Math.min(Math.round(ta.t), D) : null }])} />
        </label>
        {doc.endFrameMs != null && <>
          <NumIn label="פריים סיום (ms)" value={doc.endFrameMs} min={0} max={D} onCommit={(n) => ops([{ op: 'setBannerTiming', endFrameMs: Math.round(n) }])} />
          <button className="btn small" onClick={() => ops([{ op: 'setBannerTiming', endFrameMs: Math.round(ta.t) }])}>קבע מה-playhead</button>
        </>}
        <span className="tl-sep" />
        <label className="tl-num"><span>מהירות כוללת</span>
          <select value={speed} onChange={(e) => setSpeed(e.target.value)} aria-describedby="speed-help">
            {['0.5', '0.75', '0.9', '1.1', '1.25', '1.5', '2'].map((f) => <option key={f} value={f}>x{f} {Number(f) > 1 ? '(מהיר)' : '(איטי)'}</option>)}
          </select>
        </label>
        <button className="btn small" onClick={() => ops([{ op: 'setSpeed', factor: Number(speed) }])}>החל מהירות</button>
        <span id="speed-help" className="small muted">משנה את כל הזמנים באופן יחסי (ללא AI)</span>
      </div>
      <div className="small muted">
        משך כולל: <span className="mono">{total === Infinity ? '∞' : fmtMs(total)}</span>
        {maxD !== null ? <> · מקסימום במפרט: <span className="mono">{fmtMs(maxD)}</span></> : <> · מקסימום במפרט: לא ידוע</>}
        {maxL !== null && <> · לולאות מותרות: <span className="mono">{maxL}</span></>}
      </div>
      {warns.length > 0 && <div className="alert warn small" role="status">{warns.map((w) => <div key={w}>⚠ {w}</div>)}</div>}

      <div className="tl-grid" dir="ltr">
        <div className="tl-labels" dir="rtl">
          <div className="tl-ruler-label small muted">שכבה</div>
          {layers.map((l) => (
            <button key={l.id} className={`tl-label ${l.id === selectedId ? 'active' : ''}`} onClick={() => onSelect(l.id)} aria-pressed={l.id === selectedId}>{l.name}</button>
          ))}
        </div>
        <div className="tl-lanes" ref={lanes}>
          <div className="tl-ruler" onPointerDown={(e) => { scrub.current = true; onSeek(Math.round(msFromClientX(e.clientX))); }} aria-hidden="true">
            {ticks.map((t) => <span key={t} className="tl-tick mono" style={{ left: pct(t) }}>{t / 1000}s</span>)}
          </div>
          {layers.map((l) => {
            const s = l.startMs, e = l.endMs ?? D;
            const tr = trackOf(doc, l.id);
            const len = Math.max(1, e - s);
            return (
              <div key={l.id} className="tl-lane" onPointerDown={(ev) => { if (ev.target === ev.currentTarget) { scrub.current = true; onSeek(Math.round(msFromClientX(ev.clientX))); } }}>
                <div className={`tl-bar ${l.id === selectedId ? 'active' : ''} ${!l.visible ? 'hidden' : ''}`} style={{ left: pct(s), width: pct(len) }}
                  onPointerDown={(ev) => startDrag(ev, l, 'move')} title={`${l.name}: ${s}–${l.endMs ?? 'סוף'}ms`}>
                  {tr?.enter && tr.enter.type !== 'none' && <span className="tl-enter" style={{ width: `${Math.min(100, (tr.enter.durationMs / len) * 100)}%` }} />}
                  {tr?.exit && tr.exit.type !== 'none' && l.endMs != null && <span className="tl-exit" style={{ width: `${Math.min(100, (tr.exit.durationMs / len) * 100)}%` }} />}
                  {tr?.keyframes.map((k, i) => <span key={i} className="tl-kf" style={{ left: `${((k.atMs - s) / len) * 100}%` }} />)}
                  <span className="tl-edge start" onPointerDown={(ev) => startDrag(ev, l, 'start')} />
                  <span className="tl-edge end" onPointerDown={(ev) => startDrag(ev, l, 'end')} />
                </div>
              </div>
            );
          })}
          <div className="tl-playhead" style={{ left: pct(ta.t) }} aria-hidden="true" />
          {doc.endFrameMs != null && <div className="tl-endframe" style={{ left: pct(doc.endFrameMs) }} title="פריים סיום" aria-hidden="true" />}
        </div>
      </div>
      <p className="small muted" style={{ margin: '4px 0 0' }}>גרירת מקטע מזיזה; גרירת קצה משנה התחלה/סוף (קפיצות של 50ms, Alt = 10ms). לחיצה על הסרגל מזיזה את ה-playhead.</p>

      {sel && <LayerTiming doc={doc} l={sel} t={ta.t} ops={ops} />}
    </section>
  );
}

function TransitionEditor({ label, value, onChange }: { label: string; value: Transition | null; onChange: (t: Transition | null) => void }) {
  const v = value;
  const base: Transition = v ?? { type: 'fade', from: 'right', distance: 40, durationMs: 400, easing: 'easeOut' };
  return (
    <fieldset className="pgroup tl-tr">
      <legend>{label}</legend>
      <div className="row">
        <label className="tl-num"><span>סוג</span>
          <select value={v ? v.type : 'null'} onChange={(e) => onChange(e.target.value === 'null' ? null : { ...base, type: e.target.value as Transition['type'] })}>
            <option value="null">ללא</option><option value="fade">fade</option><option value="slide">slide</option><option value="scale">scale</option><option value="none">none (מיידי)</option>
          </select>
        </label>
        {v && v.type === 'slide' && <>
          <label className="tl-num"><span>כיוון</span>
            <select value={v.from} onChange={(e) => onChange({ ...v, from: e.target.value as Transition['from'] })}>
              <option value="right">מימין</option><option value="left">משמאל</option><option value="top">מלמעלה</option><option value="bottom">מלמטה</option>
            </select>
          </label>
          <NumIn label="מרחק (px)" value={v.distance} max={2000} step={5} width={60} onCommit={(n) => onChange({ ...v, distance: n })} />
        </>}
        {v && v.type !== 'none' && <>
          <NumIn label="משך (ms)" value={v.durationMs} max={600000} step={50} width={70} onCommit={(n) => onChange({ ...v, durationMs: Math.round(n) })} />
          <label className="tl-num"><span>האצה</span>
            <select value={v.easing} onChange={(e) => onChange({ ...v, easing: e.target.value as Transition['easing'] })}>
              <option value="linear">linear</option><option value="easeIn">easeIn</option><option value="easeOut">easeOut</option><option value="easeInOut">easeInOut</option>
            </select>
          </label>
        </>}
      </div>
    </fieldset>
  );
}

function LayerTiming({ doc, l, t, ops }: { doc: BannerDoc; l: Layer; t: number; ops: (o: any[], key?: string) => void }) {
  const tr = trackOf(doc, l.id);
  const kfs = tr?.keyframes ?? [];
  const setKfs = (next: typeof kfs) => ops([{ op: 'setKeyframes', layerId: l.id, keyframes: next }]);
  const P = ['dx', 'dy', 'scale', 'opacity', 'rotation'] as const;
  return (
    <div className="tl-detail">
      <h4 className="ed-h" style={{ margin: '8px 0 4px' }}>תזמון: {l.name}</h4>
      <div className="row">
        <NumIn label="התחלה (ms)" value={l.startMs} max={doc.durationMs} onCommit={(n) => ops([{ op: 'setTiming', layerId: l.id, startMs: Math.round(n) }])} />
        <label className="tl-num"><span>עד סוף הבאנר</span>
          <input type="checkbox" checked={l.endMs == null} onChange={(e) => ops([{ op: 'setTiming', layerId: l.id, endMs: e.target.checked ? null : doc.durationMs }])} />
        </label>
        {l.endMs != null && <NumIn label="סיום (ms)" value={l.endMs} max={600000} onCommit={(n) => ops([{ op: 'setTiming', layerId: l.id, endMs: Math.round(n) }])} />}
      </div>
      <div className="grid cols-2" style={{ gap: 6 }}>
        <TransitionEditor label="כניסה" value={tr?.enter ?? null} onChange={(v) => ops([{ op: 'setEnter', layerId: l.id, enter: v }])} />
        <div>
          <TransitionEditor label="יציאה" value={tr?.exit ?? null} onChange={(v) => ops([{ op: 'setExit', layerId: l.id, exit: v }])} />
          {tr?.exit && l.endMs == null && <div className="small muted">יציאה פועלת רק כשלשכבה יש זמן סיום.</div>}
        </div>
      </div>
      <fieldset className="pgroup">
        <legend>Keyframes <span className="muted small">(ערכים יחסיים; ריק = ממשיך מהקודם)</span></legend>
        {kfs.length > 0 && (
          <table className="table kf-table">
            <thead><tr><th>ms</th><th>dx</th><th>dy</th><th>scale</th><th>opacity</th><th>סיבוב</th><th>האצה</th><th><span className="sr-only">פעולות</span></th></tr></thead>
            <tbody>
              {kfs.map((k, i) => (
                <tr key={i}>
                  <td><input type="number" className="mono" aria-label={`זמן keyframe ${i + 1}`} value={k.atMs} min={0} step={50} onChange={(e) => { const n = Number(e.target.value); if (Number.isFinite(n)) setKfs(kfs.map((x, j) => j === i ? { ...x, atMs: Math.max(0, Math.round(n)) } : x)); }} /></td>
                  {P.map((p) => (
                    <td key={p}><input type="number" className="mono" aria-label={`${p} keyframe ${i + 1}`} value={k.props[p] ?? ''} step={p === 'scale' || p === 'opacity' ? 0.05 : 1}
                      onChange={(e) => { const raw = e.target.value; const n = Number(raw); setKfs(kfs.map((x, j) => j === i ? { ...x, props: { ...x.props, [p]: raw === '' || !Number.isFinite(n) ? undefined : n } } : x)); }} /></td>
                  ))}
                  <td><select aria-label={`האצה keyframe ${i + 1}`} value={k.easing} onChange={(e) => setKfs(kfs.map((x, j) => j === i ? { ...x, easing: e.target.value as any } : x))}>
                    <option value="linear">linear</option><option value="easeIn">easeIn</option><option value="easeOut">easeOut</option><option value="easeInOut">easeInOut</option>
                  </select></td>
                  <td><button className="btn small danger" aria-label={`מחיקת keyframe ${i + 1}`} onClick={() => setKfs(kfs.filter((_, j) => j !== i))}>✕</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <button className="btn small" disabled={kfs.length >= 50} onClick={() => setKfs([...kfs, { atMs: Math.max(l.startMs, Math.round(t)), props: { dx: 0, dy: 0, scale: 1, opacity: 1 }, easing: 'easeInOut' }])}>+ keyframe ב-playhead ({Math.round(t)}ms)</button>
      </fieldset>
    </div>
  );
}

export default memo(Timeline);
